import React, { useState, useEffect, useContext } from 'react';
import api, { clearStoredSession, getUserFacingErrorMessage, isAuthError } from '../utils/api';
import { UserContext } from '../App';
import OwnerShell from './OwnerShell';
import { isAdminInspectorAllowed } from '../utils/adminInspector';

const AUDIT_FEATURE_ENABLED = String(process.env.REACT_APP_FEATURE_AUDIT || '').trim() === '1';

export default function AuditLog() {
  const { currentUser, setCurrentUser } = useContext(UserContext);
  const [log, setLog] = useState([]);
  const [error, setError] = useState('');

  // Asset owner is the current logged-in user
  const assetHolder = currentUser;
  if (!assetHolder) {
    return <div>Loading...</div>;
  }

  const allowed = AUDIT_FEATURE_ENABLED && isAdminInspectorAllowed(currentUser);
  if (!allowed) {
    return (
      <OwnerShell>
        <div style={{ maxWidth: 960, margin: '0 auto', padding: '20px 20px 40px' }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 16, boxShadow: 'var(--shadow-soft)' }}>
            <h1 style={{ marginTop: 0 }}>Audit Log</h1>
            <div style={{ color: '#444' }}>
              This page is disabled.
            </div>
          </div>
        </div>
      </OwnerShell>
    );
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setError('');
        // Server enforces owner scoping for non-admin callers.
        const res = await api.get('/audit', { params: { days: 7, limit: 200 } });
        if (cancelled) return;
        const rows = Array.isArray(res.data) ? res.data : [];
        setLog(rows);
      } catch (err) {
        if (cancelled) return;
        console.error('AuditLog: failed to load audit entries', err?.response?.data || err);
        if (isAuthError(err)) {
          clearStoredSession();
          setCurrentUser(null);
        } else {
          setError(getUserFacingErrorMessage(err, {
            fallback: 'We could not load audit entries. Please try again.',
            includeSupportForUnknown: false,
          }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [setCurrentUser]);

  return (
    <OwnerShell>
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '20px 20px 40px' }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: 16, boxShadow: 'var(--shadow-soft)' }}>
          <h1 style={{ marginTop: 0 }}>Audit Log</h1>
          <div style={{ color: '#555', marginBottom: 10, fontSize: 13 }}>
            Showing the most recent audit entries for your account.
          </div>
          {error && <div style={{ color: '#b00020', fontWeight: 700, marginBottom: 10 }}>{error}</div>}
          {log.length === 0 ? (
            <div style={{ color: '#666' }}>No audit entries yet.</div>
          ) : (
            <div style={{ overflowX: 'auto', border: '1px solid var(--mist-300)', borderRadius: 12 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead style={{ background: 'var(--mist-200)' }}>
                  <tr>
                    {['Time', 'Action', 'Result', 'Asset ID', 'User ID'].map((h) => (
                      <th key={h} style={{ textAlign: 'left', padding: '10px 10px', borderBottom: '1px solid var(--mist-300)', whiteSpace: 'nowrap' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {log.map((e) => (
                    <tr key={e.id || `${e.timestamp || ''}:${e.action || ''}`}>
                      <td style={{ padding: '10px 10px', borderBottom: '1px solid var(--mist-200)', whiteSpace: 'nowrap' }}>
                        {e.timestamp || '-'}
                      </td>
                      <td style={{ padding: '10px 10px', borderBottom: '1px solid var(--mist-200)', whiteSpace: 'nowrap' }}>
                        {e.action || '-'}
                      </td>
                      <td style={{ padding: '10px 10px', borderBottom: '1px solid var(--mist-200)', whiteSpace: 'nowrap' }}>
                        {e.result || '-'}
                      </td>
                      <td style={{ padding: '10px 10px', borderBottom: '1px solid var(--mist-200)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace', fontSize: 12 }}>
                        {e.assetId || '-'}
                      </td>
                      <td style={{ padding: '10px 10px', borderBottom: '1px solid var(--mist-200)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace', fontSize: 12 }}>
                        {e.userId || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </OwnerShell>
  );
}